import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumberParam, StringParam, useQueryParam } from 'use-query-params'
import { Button, SearchComponent, Select, StartPage } from '../../components'
import { dedicatedServersService } from '../../api'
import { useUi } from '../../context/ui.context'
import { usePageTitle } from '../../context/navigationContext'
import useUpdateEffect from '../../hooks/useUpdateEffect'
import { extractErrorMessage } from '../../utils/error-extractor'
import { formatDate } from '../../utils/date-formatter'
import { DedicatedUser, PaginatedDedicatedUsers } from './types'
import {
  FilterActions,
  Filters,
  FilterRow,
  FilterSelect,
  ListPage,
  SearchField,
  SearchRow,
  Status,
  StatusDot,
} from './styles'

const getObjectDisplay = (value: Record<string, unknown> | null) => {
  if (!value) return '-'
  const display = value.display || value.full_name || value.name || value.company || value.email || value.id
  return display !== undefined && display !== null ? String(display) : '-'
}

const DedicatedUsers: FC = () => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [users, setUsers] = useState<DedicatedUser[]>([])
  const [paginationData, setPaginationData] = useState<PaginatedDedicatedUsers | null>(null)
  const [pageLoading, setPageLoading] = useState(true)

  const [page, setPage] = useQueryParam('page', NumberParam)
  const [pageSize] = useQueryParam('page_size', NumberParam)
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam)
  const [search, setSearch] = useQueryParam('search', StringParam)
  const [disabled, setDisabled] = useQueryParam('disabled', StringParam)
  const [deleted, setDeleted] = useQueryParam('deleted', StringParam)
  const [disabledReason, setDisabledReason] = useQueryParam('disabled_reason', StringParam)

  usePageTitle(String(t('DEDICATED_USERS')))

  const updateUsers = useCallback(() => {
    setPageLoading(true)
    dedicatedServersService.getUsers({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || undefined,
      search: search || undefined,
      disabled: disabled || undefined,
      deleted: deleted || undefined,
      disabled_reason: disabledReason || undefined,
    })
      .then((response) => {
        setUsers(response.data.objects)
        setPaginationData(response.data)
      })
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('FAILED_TO_LOAD_DEDICATED_USERS'),
        })
      })
      .finally(() => setPageLoading(false))
  }, [deleted, disabled, disabledReason, ordering, page, pageSize, search, t, viewAlert])

  useEffect(() => {
    updateUsers()
  }, [updateUsers])

  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, search, disabled, deleted, disabledReason])

  const accountOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'false', value: 'false', label: t('DEDICATED_ENABLED_USERS') },
    { id: 'true', value: 'true', label: t('DEDICATED_DISABLED_USERS') },
  ], [t])

  const recordOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'false', value: 'false', label: t('DEDICATED_ACTIVE_RECORDS') },
    { id: 'true', value: 'true', label: t('DEDICATED_DELETED_RECORDS') },
  ], [t])

  const reasonOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'need_payment', value: 'need_payment', label: t('DEDICATED_NEED_PAYMENT') },
    { id: 'admin_locked', value: 'admin_locked', label: t('DEDICATED_ADMIN_LOCKED') },
  ], [t])

  const reasonLabel = useCallback((reason: DedicatedUser['disabled_reason']) => {
    if (reason === 'need_payment') return t('DEDICATED_NEED_PAYMENT')
    if (reason === 'admin_locked') return t('DEDICATED_ADMIN_LOCKED')
    return '-'
  }, [t])

  const columns = useMemo(() => [
    { title: t('EXTERNAL_ID'), key: 'name' },
    { title: t('CLIENT'), key: 'client' },
    { title: t('SERVICE'), key: 'service' },
    { title: t('SERVICE_STATUS'), key: 'service_status' },
    { title: t('STATUS'), key: 'account_status' },
    { title: t('DEDICATED_DISABLED_REASON'), key: 'disabled_reason' },
    { title: t('CREATED_AT'), key: 'created_at', sorted: true },
    { title: t('UPDATED_AT'), key: 'updated_at', sorted: true },
  ], [t])

  const rows = useMemo(() => users.map((user) => {
    const status = user.deleted ? 'deleted' : user.disabled ? 'disabled' : 'active'
    const statusText = user.deleted
      ? t('DEDICATED_DELETED')
      : user.disabled ? t('DISABLED') : t('ENABLED')
    return {
      ...user,
      name: String(user.external_id),
      client: getObjectDisplay(user.client),
      service: user.service?.display_name || user.service?.domain_name || (user.service?.id ? `#${user.service.id}` : '-'),
      service_status: user.service?.status || '-',
      account_status: (
        <Status status={status}>
          <StatusDot status={status} />
          {statusText}
        </Status>
      ),
      disabled_reason: reasonLabel(user.disabled_reason),
      created_at: formatDate(user.created_at),
      updated_at: formatDate(user.updated_at),
      navigate: `/dedicated-servers/users/${user.id}`,
    }
  }), [reasonLabel, t, users])

  const clearFilters = () => {
    setSearch(undefined)
    setDisabled(undefined)
    setDeleted(undefined)
    setDisabledReason(undefined)
    setOrdering(undefined)
    setPage(1)
  }

  const hasFilters = Boolean(search || disabled || deleted || disabledReason || ordering)

  return (
    <ListPage>
      <StartPage
      title={String(t('DEDICATED_USERS'))}
      noResourceText={String(t('NO_DEDICATED_USERS'))}
      haveResource={users.length > 0}
      pageLoading={pageLoading}
      withoutCreateResourceButton
      rightCustomCreateButton={(
        <Filters>
          <SearchRow>
            <SearchField>
              <SearchComponent />
            </SearchField>
            <FilterActions>
              <Button
                title={t('CLEAR_FILTERS')}
                onClick={clearFilters}
                variant="stroke"
                size="display"
                disabled={!hasFilters}
              />
            </FilterActions>
          </SearchRow>
          <FilterRow>
            <FilterSelect>
              <Select
                title={t('DEDICATED_ACCOUNT_STATE')}
                data={accountOptions}
                selectedValue={disabled || ''}
                onChange={(value) => setDisabled(value || undefined)}
              />
            </FilterSelect>
            <FilterSelect>
              <Select
                title={t('DEDICATED_RECORD_STATE')}
                data={recordOptions}
                selectedValue={deleted || ''}
                onChange={(value) => setDeleted(value || undefined)}
              />
            </FilterSelect>
            <FilterSelect>
              <Select
                title={t('DEDICATED_DISABLED_REASON')}
                data={reasonOptions}
                selectedValue={disabledReason || ''}
                onChange={(value) => setDisabledReason(value || undefined)}
              />
            </FilterSelect>
          </FilterRow>
        </Filters>
      )}
      tableData={{
        columns,
        data: rows,
        paginationData,
        updateData: updateUsers,
        hideCheckbox: true,
      }}
      />
    </ListPage>
  )
}

export default DedicatedUsers
